import Typography from '@mui/material/Typography';
import { Column, Row } from 'further-ui/components/Layout';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import TypographyButton from 'further-ui/components/TypographyButton';
import { BankAccountOperationStatusLabels } from 'further-ui/labels';
import {
  numberToCurrencyString,
  numberToPercentageString,
} from 'further-ui/utils';
import { useState } from 'react';
import { CommitmentData } from '../../types';

type Props = Pick<CommitmentData, 'currencySettings' | 'capitalActions'>;

const CapitalHistory: React.FC<Props> = ({
  currencySettings,
  capitalActions,
}) => {
  const { currencyLcy } = currencySettings;
  const {
    fullDetails: { data: fullDetails, totals },
    summary,
  } = capitalActions;
  const [showFullBreakdown, setShowFullBreakdown] = useState(false);

  const toggleFullBreakdown = () => {
    setShowFullBreakdown((prev) => !prev);
  };

  const summaryColumns: Array<TableColumn<(typeof summary)[number]>> = [
    {
      key: 'date',
      label: 'Date',
      render: (row) => row.date.toReadable(),
    },
    {
      key: 'name',
      label: 'Name',
      render: (row) => row.name,
    },
    {
      key: 'capitalCallLcy',
      label: 'Capital call',
      render: (row) =>
        numberToCurrencyString(row.capitalCallLcy, {
          currency: currencyLcy,
        }),
    },
    {
      key: 'capitalRefundLcy',
      label: 'Distribution/capital refund',
      render: (row) =>
        numberToCurrencyString(row.capitalRefundLcy, {
          currency: currencyLcy,
        }),
    },
    {
      key: 'netCallLcy',
      label: 'Net call/(distribution)',
      render: (row) =>
        numberToCurrencyString(row.netCallLcy, {
          currency: currencyLcy,
        }),
    },
    {
      key: 'reconciliationStatus',
      label: 'Reconciliation status',
      render: (row) =>
        BankAccountOperationStatusLabels[row.reconciliationStatus],
    },
  ];

  const fullDetailsColumns: Array<TableColumn<(typeof fullDetails)[number]>> = [
    {
      key: 'date',
      label: 'Date',
      render: (row) => row.date.toReadable(),
      width: 150,
    },
    {
      key: 'name',
      label: 'Name',
      render: (row) => row.name,
      width: 250,
      withVerticalBorder: true,
    },
    {
      key: 'insideCommitmentAmountLcy',
      label: 'Inside commitment amount',
      width: 210,
      render: (row) =>
        row.insideCommitmentAmountLcy ? (
          numberToCurrencyString(row.insideCommitmentAmountLcy, {
            currency: currencyLcy,
          })
        ) : (
          <Typography variant="inherit" color="grey.500">
            --
          </Typography>
        ),
      footer: numberToCurrencyString(totals.insideCommitmentAmountLcy, {
        currency: currencyLcy,
      }),
    },
    {
      key: 'outsideCommitmentAmountLcy',
      label: 'Outside commitment amount',
      width: 230,
      render: (row) =>
        row.outsideCommitmentAmountLcy ? (
          numberToCurrencyString(row.outsideCommitmentAmountLcy, {
            currency: currencyLcy,
          })
        ) : (
          <Typography variant="inherit" color="grey.500">
            --
          </Typography>
        ),
      footer: numberToCurrencyString(totals.outsideCommitmentAmountLcy, {
        currency: currencyLcy,
      }),
    },
    {
      key: 'recalledCapitalLcy',
      label: 'Recalled capital',
      width: 150,
      render: (row) =>
        row.recalledCapitalLcy ? (
          numberToCurrencyString(row.recalledCapitalLcy, {
            currency: currencyLcy,
          })
        ) : (
          <Typography variant="inherit" color="grey.500">
            --
          </Typography>
        ),
      footer: numberToCurrencyString(totals.recalledCapitalLcy, {
        currency: currencyLcy,
      }),
    },
    {
      key: 'equalisationInterestLcy',
      label: 'Equalisation interest',
      width: 180,
      withVerticalBorder: true,
      render: (row) =>
        row.equalisationInterestLcy ? (
          numberToCurrencyString(row.equalisationInterestLcy, {
            currency: currencyLcy,
          })
        ) : (
          <Typography variant="inherit" color="grey.500">
            --
          </Typography>
        ),
      footer: numberToCurrencyString(totals.equalisationInterestLcy, {
        currency: currencyLcy,
      }),
    },
    {
      key: 'notionalPreCarryDistributionLcy',
      label: 'Notional pre-carry distribution',
      width: 240,
      render: (row) =>
        row.notionalPreCarryDistributionLcy ? (
          numberToCurrencyString(row.notionalPreCarryDistributionLcy, {
            currency: currencyLcy,
          })
        ) : (
          <Typography variant="inherit" color="grey.500">
            --
          </Typography>
        ),
      footer: numberToCurrencyString(totals.notionalPreCarryDistributionLcy, {
        currency: currencyLcy,
      }),
    },
    {
      key: 'notionalCarriedInterestLcy',
      label: 'Notional carried interest',
      width: 200,
      render: (row) =>
        row.notionalCarriedInterestLcy ? (
          numberToCurrencyString(row.notionalCarriedInterestLcy, {
            currency: currencyLcy,
          })
        ) : (
          <Typography variant="inherit" color="grey.500">
            --
          </Typography>
        ),
      footer: numberToCurrencyString(totals.notionalCarriedInterestLcy, {
        currency: currencyLcy,
      }),
    },
    {
      key: 'netDistributionInsideLcy',
      label: 'Net distribution (inside)',
      width: 200,
      render: (row) =>
        row.netDistributionInsideLcy ? (
          numberToCurrencyString(row.netDistributionInsideLcy, {
            currency: currencyLcy,
          })
        ) : (
          <Typography variant="inherit" color="grey.500">
            --
          </Typography>
        ),
      footer: numberToCurrencyString(totals.netDistributionInsideLcy, {
        currency: currencyLcy,
      }),
    },
    {
      key: 'recallableDistributionLcy',
      label: 'Recallable distribution (inside)',
      width: 230,
      render: (row) =>
        row.recallableDistributionLcy ? (
          numberToCurrencyString(row.recallableDistributionLcy, {
            currency: currencyLcy,
          })
        ) : (
          <Typography variant="inherit" color="grey.500">
            --
          </Typography>
        ),
      footer: numberToCurrencyString(totals.recallableDistributionLcy, {
        currency: currencyLcy,
      }),
    },
    {
      key: 'netDistributionOutsideLcy',
      label: 'Net distribution (outside)',
      width: 210,
      withVerticalBorder: true,
      render: (row) =>
        row.netDistributionOutsideLcy ? (
          numberToCurrencyString(row.netDistributionOutsideLcy, {
            currency: currencyLcy,
          })
        ) : (
          <Typography variant="inherit" color="grey.500">
            --
          </Typography>
        ),
      footer: numberToCurrencyString(totals.netDistributionOutsideLcy, {
        currency: currencyLcy,
      }),
    },
    {
      key: 'priorUnfundedCommitmentLcy',
      label: 'Prior unfunded commitment',
      width: 230,
      render: (row) =>
        numberToCurrencyString(row.priorUnfundedCommitmentLcy, {
          currency: currencyLcy,
        }),
    },
    {
      key: 'newUnfundedCommitmentLcy',
      label: 'New unfunded commitment',
      width: 230,
      render: (row) =>
        numberToCurrencyString(row.newUnfundedCommitmentLcy, {
          currency: currencyLcy,
        }),
    },
    {
      key: 'unfundedCommitmentPercentage',
      label: 'Unfunded commitment (%)',
      width: 230,
      render: (row) =>
        numberToPercentageString(row.unfundedCommitmentPercentage),
    },
  ];

  return (
    <Column spacing="xl">
      <Table
        emptyMessage="No entries"
        columns={summaryColumns}
        rows={summary}
      />
      <Column spacing="sm">
        <Row spacing="xs">
          {summary.length > 0 && showFullBreakdown ? (
            <Typography variant="h5">Full capital action breakdown</Typography>
          ) : null}
          {summary.length > 0 ? (
            <TypographyButton
              disableRipple
              onClick={toggleFullBreakdown}
              text={
                showFullBreakdown
                  ? 'Hide'
                  : 'Show full capital action breakdown'
              }
            />
          ) : null}
        </Row>
        {summary.length > 0 && showFullBreakdown ? (
          <Table scrollable rows={fullDetails} columns={fullDetailsColumns} />
        ) : null}
      </Column>
    </Column>
  );
};

export default CapitalHistory;
